/**
 * `/production` — the production / WIP board (roadmap Phase 2).
 *
 * A single-pane view of every open order, laned by production status: Needs action → Queued → In
 * production → Printed·to ship. It exists because once a line is claimed into a gang sheet it drops
 * off the Orders queue and is invisible until the order ships — so batched/printed work had nowhere to
 * be seen. Terminal orders (shipped/cancelled) fall off automatically.
 *
 * The ship lanes carry a guarantee: an order with an outstanding reprint is never in one (the server
 * demotes it to In production — decision D4 / §8.25). `reprintCheckFailed` says when that guarantee is
 * suspended, and the banner below is how the page stops implying it still holds.
 *
 * Read-only, and CHANGE-FEED DRIVEN — not manual-refresh-only, which is what this comment used to say.
 * `production-board` is registered under BOTH the `orders` and `batches` domains in `lib/change-feed.ts`
 * and is absent from `MARK_STALE_ONLY`, so the watcher refetches it (default `refetchType: 'active'`)
 * whenever either domain moves, floored at the watcher's 5s throttle. The Refresh button is the manual
 * backstop for a dead socket, not the only path to a re-run. There is no `refetchInterval` — "no
 * polling" was and remains true; "only on Refresh" never was.
 *
 * The read is a GSI2 Query over the whole `GSI2PK='ORDERLINE'` partition (ORDER_LINES_GSI2_READ='true'
 * in prod), degrading to a full-table Scan only if that kill-switch is flipped. Either way the cost
 * scales with total order HISTORY rather than open work, and it is paid per feed event per open tab —
 * so "we keep the request frequency low" is not something this page does. Bounding it is a separate
 * piece of work; do NOT bound it by adding this key to MARK_STALE_ONLY, whose members are there for a
 * money reason (a refetch reordering rows under a finger that fires a consume) that does not apply
 * here — this page has no mutations at all.
 *
 * Laning + sorting are done server-side; this page only renders.
 */
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { AlertTriangle, CheckCircle2, Factory } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { PAGE_CONTAINER_CLASS } from '@/components/page-container';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useIsMobile } from '@/hooks/use-mobile';
import { useApiClient } from '@/lib/api/hooks';
import { cn } from '@/lib/utils';
import { orderCardSearch } from './order-detail-lib';
import { dueStatus, relativeAge } from './orders-queue';
import {
  type BoardFilter,
  boardFilterActive,
  boardPieceTotal,
  boardStoreOptions,
  LANES,
  type LaneSelection,
  matchesBoardFilter,
  type ProductionBoardOrder,
  productionCountLine,
  productionPieceLine,
  SHEET_STATUS_LABEL,
  selectedLane,
  sheetLabel,
} from './production-board-lib';

export function ProductionBoardPage() {
  const client = useApiClient();

  const boardQuery = useQuery({
    queryKey: ['production-board'],
    queryFn: async () => {
      const { data, error } = await client.GET('/production/board');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load the board.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });

  const board = boardQuery.data;
  const nowMs = Date.now();
  /** Clock time of the last successful load — 0 until one lands, so it renders nothing while loading. */
  const updatedAt = boardQuery.dataUpdatedAt
    ? new Date(boardQuery.dataUpdatedAt).toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
      })
    : null;

  // Lane filter. In the URL (loose pattern — this route has no validateSearch, like every other route
  // in this app) so a lane is linkable and survives a refresh.
  const {
    lane: laneParam,
    q: qParam,
    store: storeParam,
  } = useSearch({ strict: false }) as { lane?: string; q?: string; store?: string };
  const navigate = useNavigate();
  const isNarrow = useIsMobile();

  const filter: BoardFilter = { q: qParam, store: storeParam };
  const filtering = boardFilterActive(filter);
  const storeOptions = board ? boardStoreOptions(board) : [];
  const pieceTotal = board ? boardPieceTotal(board) : 0;
  /** Per-lane orders after the filter — computed once and reused by the chips, badges and cards. */
  const shown = {
    needsAction: (board?.needsAction ?? []).filter((o) => matchesBoardFilter(o, filter)),
    queued: (board?.queued ?? []).filter((o) => matchesBoardFilter(o, filter)),
    inProduction: (board?.inProduction ?? []).filter((o) => matchesBoardFilter(o, filter)),
    printed: (board?.printed ?? []).filter((o) => matchesBoardFilter(o, filter)),
    readyToShip: (board?.readyToShip ?? []).filter((o) => matchesBoardFilter(o, filter)),
  };
  // Lane landing follows the FILTERED counts: with a filter on, "the first lane with work" should mean
  // the first lane with work you can actually see.
  const counts = {
    needsAction: shown.needsAction.length,
    queued: shown.queued.length,
    inProduction: shown.inProduction.length,
    printed: shown.printed.length,
    readyToShip: shown.readyToShip.length,
  };
  const setFilter = (next: BoardFilter) => {
    navigate({
      to: '/production',
      // Object form REPLACES the params, so the lane has to be carried explicitly or changing the
      // search box would silently reset which lane you were looking at.
      search: {
        ...(laneParam ? { lane: laneParam } : {}),
        ...(next.q?.trim() ? { q: next.q.trim() } : {}),
        ...(next.store ? { store: next.store } : {}),
      },
      replace: true,
    });
  };
  const lane = selectedLane(laneParam, counts, isNarrow);
  const setLane = (next: LaneSelection) => {
    // Toggling the selected lane off returns to the whole board; on a phone `selectedLane` resolves
    // that straight back to a single lane, so the chip reads as a no-op there rather than dumping the
    // operator into the five-stacked-lanes layout this replaces.
    navigate({
      to: '/production',
      // Same replace-not-merge caveat as setFilter: the search box and store have to be carried, or
      // picking a lane would silently clear the filter the operator just typed.
      search: {
        ...(next === lane ? {} : { lane: next }),
        ...(qParam ? { q: qParam } : {}),
        ...(storeParam ? { store: storeParam } : {}),
      },
      replace: true,
    });
  };
  const shownLanes = lane === 'all' ? LANES : LANES.filter((l) => l.key === lane);

  // ⚠️ The chip strip SCROLLS on a phone, so the selected chip can start off-screen: land on
  // ?lane=readyToShip and the strip reads "Needs action / Queued …" with nothing saying which lane
  // you are looking at. `inline: 'nearest'` scrolls the STRIP; `block: 'nearest'` stops it scrolling
  // the PAGE, which must not move under an operator who did not ask it to.
  const selectedChipRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    selectedChipRef.current?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
  }, []);

  return (
    <div className={cn(PAGE_CONTAINER_CLASS, 'space-y-6')}>
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Factory className="size-6" /> Production
          </h1>
          {/* Desktop only. Measured at phone width this is 3 lines of prose ABOUT the page sitting
              above the work itself, and 77% of a 780px screen was chrome before the first card. It
              explains what the board is — a thing you read once — while the lane blurbs below carry
              the same information at the moment it is operational. */}
          <p className="hidden text-sm text-muted-foreground md:block">
            Every open order by where it stands — including batched and printed work that drops off
            the Orders queue. Orders fall off once they ship in Shopify.
          </p>
        </div>
        {/* ⚠️ NO REFRESH BUTTON AND NO "Updated HH:MM" — owner decision 2026-08-09, taken with the
            trade-off stated. The board is PUSH-DRIVEN: `production-board` is subscribed to the change
            feed under BOTH the `orders` and `batches` domains (`lib/change-feed.ts`), so it
            invalidates on every line-state and batch write without anyone pressing anything, and the
            pair cost a whole row on a phone to offer a button nobody needs to press.
            The cost, accepted: if the socket dies the board freezes with nothing on screen saying so.
            Recovery is a page reload — pull-to-refresh on iOS. Do NOT re-add a bare Refresh button on
            a "the data might be stale" argument; that argument was made and answered. If staleness
            needs surfacing again, surface it as a WARNING when the socket is known-dead, not as a
            control an operator has to remember to press.
            ⚠️ `updatedAt` itself STAYS — the error path below still says "showing the board as of
            HH:MM", which is an explanation of a FAILED refetch, not header chrome. */}
      </header>

      {/* Shipped in Shopify but NOT produced here — these vanish from the queue and the four lanes the
          moment they ship, so surface them prominently. */}
      {board && board.shippedUnproduced.length > 0 && (
        <section className="space-y-3 rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-900/60 dark:bg-amber-950/30">
          <div className="flex items-center gap-2">
            <AlertTriangle className="size-5 shrink-0 text-amber-600 dark:text-amber-400" />
            <h2 className="text-sm font-semibold">
              Shipped in Shopify — not finished here ({board.shippedUnproduced.length})
            </h2>
          </div>
          <p className="text-xs text-muted-foreground">
            These orders were in production — on a gang sheet — but Shopify shipped them before they
            were marked printed, so they dropped off the queue and the lanes below. Finish and mark
            them printed, or confirm they shipped correctly.
          </p>
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {board.shippedUnproduced.map((order) => (
              <OrderCard key={`su-${order.storeId}#${order.orderId}`} order={order} nowMs={nowMs} />
            ))}
          </div>
        </section>
      )}

      {/* The outstanding-reprint lookup failed, so decision D1 demotes EVERY printed / produced / packed
          order into `inProduction` (production-board.ts:180). The two ship lanes therefore come back
          PROVABLY EMPTY.

          The old copy here told the operator to "open an order in Printed · to ship to check before
          shipping" — pointing at a lane the same failure had just emptied, while their finished work sat
          under a heading saying it was still printing with no per-card marker (`reprintOutstanding` is
          hard-zeroed on failure, so the amber badge cannot render). Amber, not destructive: the demotion
          is the fail-SAFE direction and nothing is broken; red is this app's "can't print it" colour. */}
      {board?.reprintCheckFailed && (
        <Alert className="border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200 [&>svg]:text-amber-600 dark:[&>svg]:text-amber-400">
          <AlertTriangle className="size-4" />
          <AlertTitle>Reprint check unavailable — nothing can be shown as ready to ship</AlertTitle>
          <AlertDescription className="text-amber-900/90 dark:text-amber-200/90">
            We couldn’t look up outstanding reprints, so printed orders are being held in “In
            production” until the check succeeds. Hit Refresh, or open an order and confirm no
            reprint is on the press before you bag it.
          </AlertDescription>
        </Alert>
      )}

      {board && board.totalOrders > 0 && (
        <p className="text-sm text-muted-foreground">
          {filtering ? (
            <>
              Showing{' '}
              <span className="font-medium text-foreground">
                {Object.values(counts).reduce((a, b) => a + b, 0)}
              </span>{' '}
              of <span className="font-medium text-foreground">{board.totalOrders}</span> orders in
              the pipeline.
            </>
          ) : (
            <>
              <span className="font-medium text-foreground">{board.totalOrders}</span> order
              {board.totalOrders === 1 ? '' : 's'}
              {/* GARMENTS, not just orders. "67 orders" says nothing about the size of the day — 67
                  one-shirt orders and 67 fifty-shirt orders are the same number and a very different
                  week. The per-order counts landed in PR-8; this is their total. */}
              {pieceTotal > 0 && (
                <>
                  {' · '}
                  <span className="font-medium text-foreground">{pieceTotal}</span> piece
                  {pieceTotal === 1 ? '' : 's'}
                </>
              )}{' '}
              in the pipeline.
            </>
          )}
        </p>
      )}

      {/* Initial-load failure (no data to fall back on) → the full error. */}
      {boardQuery.isError && !board && (
        <Alert variant="destructive">
          <AlertTitle>Couldn’t load the board</AlertTitle>
          <AlertDescription>
            {boardQuery.error instanceof Error
              ? boardQuery.error.message
              : 'Unknown error. Try refreshing.'}
          </AlertDescription>
        </Alert>
      )}

      {/* Refresh failed but we still have the last-loaded board → keep it, and say WHEN it landed.
          "the last loaded board" is not a time: the operator could not tell two minutes from two hours.
          Amber rather than destructive for the same reason as the banner above — the data is stale but
          usable, which is not this app's red. */}
      {boardQuery.isError && board && (
        <p className="text-sm text-amber-700 dark:text-amber-400">
          Couldn’t refresh{updatedAt ? ` — showing the board as of ${updatedAt}` : ''}. Work may
          have moved since.
        </p>
      )}

      {boardQuery.isLoading && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          {LANES.map((lane) => (
            <Skeleton key={lane.key} className="h-64 w-full" />
          ))}
        </div>
      )}

      {/* ⚠️ Gated on `totalOrders === 0` ALONE. It used to also require `shippedUnproduced.length === 0`,
          but `shippedUnproduced` is deliberately EXCLUDED from `totalOrders` (production-board.ts:225),
          so in exactly the state the amber section above exists to catch — nothing in the pipeline, one
          order shipped unfinished — the page rendered the warning and then NOTHING: no lanes (that grid
          needs `totalOrders > 0`), no counts, no empty state. It read as a page that failed to load. */}
      {board && board.totalOrders === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <CheckCircle2 className="size-8 text-emerald-600 dark:text-emerald-400" />
            <p className="font-medium">
              {board.shippedUnproduced.length > 0
                ? 'Nothing else in production'
                : 'Nothing in production'}
            </p>
            <p className="text-sm text-muted-foreground">
              {board.shippedUnproduced.length > 0
                ? 'No open orders in the pipeline — but the orders above still need finishing or confirming.'
                : 'No open orders. New work shows up here as orders come in and move through printing.'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* `items-start` so each lane sizes to its OWN content. As grid items in one row they all
          stretched to the tallest, so a 1-card "Needs action" lane inherited the height of a 30-card
          neighbour — thousands of pixels of whitespace on the live board.

          5-up moved from `xl` (1280px) to `2xl` (1536px). At a 1280px viewport the content area is
          1280 − 256 (sidebar) − 64 (lg:px-8) = 960px, i.e. five ~179px tracks; `Badge` is
          `w-fit shrink-0 whitespace-nowrap`, so a badge like "2 shipped · not printed" can neither
          shrink nor wrap and spills into the next lane. `min-w-0` on the section is the paired fix. */}
      {/* The chip strip does double duty: on a phone it IS the lane selector (five stacked lanes is
          what it replaces), and everywhere it shows the board's shape in one glance — which the
          single-column stack destroyed and which is the whole point of a board. Same `fieldset` +
          rounded chip pattern /inventory already ships. `All lanes` is desktop-only: on a phone "all"
          means the stacked layout, so offering it would offer the bug.

          ⚠️ ONE SCROLLING ROW ON A PHONE, not a wrapped block. Measured on a real iPhone at 390px:
          the five chips need 670px, so `flex-wrap` folded them into THREE rows — 144px, a sixth of
          the screen, to say what one scrolling row says. `overflow-x-auto` scrolls the STRIP, never
          the page (a board must never scroll sideways). The `-mx-*`/`px-*` pair lets a chip bleed to
          the true screen edge, so a half-visible chip cues that more exist rather than the row
          looking like it ends. From `md:` up they fit, so it wraps exactly as before.

          ⚠️ `min-w-0` IS LOAD-BEARING AND NON-OBVIOUS. A `<fieldset>` carries a UA default of
          `min-inline-size: min-content`, so it CANNOT shrink below its content — measured, the strip
          stayed 696px wide inside a 358px box and `overflow-x-auto` never engaged. It would not have
          scrolled; it would have pushed the PAGE sideways on a real phone, which is the one thing
          this board must never do. Without `min-w-0` this whole change is worse than what it
          replaced. */}
      {board && board.totalOrders > 0 && (
        <fieldset className="-mx-4 flex min-w-0 gap-1.5 overflow-x-auto px-4 [scrollbar-width:none] sm:-mx-6 sm:px-6 md:mx-0 md:flex-wrap md:overflow-visible md:px-0 [&::-webkit-scrollbar]:hidden">
          <legend className="sr-only">Show one lane</legend>
          {!isNarrow && (
            <Button
              variant={lane === 'all' ? 'default' : 'outline'}
              size="sm"
              className="h-11 shrink-0 rounded-full md:h-8"
              onClick={() => setLane('all')}
            >
              All lanes
              <span
                className={cn(
                  'tabular-nums',
                  lane === 'all' ? 'opacity-70' : 'text-muted-foreground',
                )}
              >
                {board.totalOrders}
              </span>
            </Button>
          )}
          {LANES.map((l) => (
            <Button
              key={l.key}
              ref={lane === l.key ? selectedChipRef : undefined}
              variant={lane === l.key ? 'default' : 'outline'}
              size="sm"
              className="h-11 shrink-0 rounded-full md:h-8"
              onClick={() => setLane(l.key)}
            >
              {l.title}
              <span
                className={cn(
                  'tabular-nums',
                  lane === l.key ? 'opacity-70' : 'text-muted-foreground',
                )}
              >
                {counts[l.key]}
              </span>
            </Button>
          ))}
        </fieldset>
      )}

      {/* The board is the ONLY surface that can see an order once it is on a gang sheet, so "where is
          #L-7263?" had no answer anywhere in the app except eyeballing five columns. Client-side over
          the response we already hold — no new request. No rush/overdue filter: 0 of 981 lines in the
          ledger carry `rush` or `dueDate`, and the server already sorts rush-first, so it would be a
          control that never does anything.

          Side by side even at 390px: as two full-width rows this block cost 96px to hold two
          controls that fit in 44. `min-w-0 flex-1` lets the search box yield rather than force a
          wrap; the store select takes a fixed 128px, enough for the longest brand here. */}
      {board && board.totalOrders > 0 && (
        <div className="flex gap-2">
          <Input
            type="search"
            value={qParam ?? ''}
            onChange={(e) => setFilter({ ...filter, q: e.target.value })}
            placeholder="Find an order — #L-7263"
            aria-label="Find an order"
            className="h-11 min-w-0 flex-1 sm:w-72 sm:flex-none md:h-9"
          />
          {/* Only worth a control when there is a choice to make. */}
          {storeOptions.length > 1 && (
            <Select
              value={storeParam ?? 'all'}
              onValueChange={(v) => setFilter({ ...filter, store: v === 'all' ? undefined : v })}
            >
              <SelectTrigger
                // ⚠️ `data-[size=default]:h-11`, NOT a bare `h-11`. Measured in Chrome: the bare
                // version rendered 36px on a phone while the Input beside it rendered 44px, because
                // SelectTrigger's own base classes carry `data-[size=default]:h-9` and the element
                // has data-size="default". `[data-size=default]` is specificity (0,2,0), `.h-11` is
                // (0,1,0) — the primitive wins, and twMerge CANNOT dedupe them because they are
                // different variant prefixes, so the class string reads correct and silently loses.
                // Matching the variant is what actually raises it to the 44px phone floor.
                className="w-32 shrink-0 data-[size=default]:h-11 sm:w-44 md:data-[size=default]:h-9"
                aria-label="Filter by store"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All stores</SelectItem>
                {/* value = the raw id (it is the filter key and lives in `?store=`); the LABEL is
                    what the operator reads. Keeping them separate is why renaming a brand in /stores
                    cannot break a saved board link. */}
                {storeOptions.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      {board && board.totalOrders > 0 && (
        <div
          className={cn(
            // Whole board → five lanes as columns. One lane → no outer grid at all; the lane owns the
            // full width and spreads its own cards below, so 30 orders read as rows instead of one
            // tall ribbon.
            lane === 'all'
              ? 'grid items-start gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5'
              : 'space-y-3',
          )}
        >
          {shownLanes.map((lane) => {
            const orders = shown[lane.key];
            const total = board[lane.key].length;
            return (
              <section
                key={lane.key}
                aria-labelledby={`lane-${lane.key}`}
                // A LANE IS A CONTAINER, not a run of loose text. Measured against the approved mockup,
                // which has `1px solid`, `radius 10px` and a tinted ground here — the shipped version
                // had none of it, and two things followed that only showed up on screen:
                //   · `justify-between` pushed each lane's count ~386px from its own title at desktop
                //     width, so the heading read as floating text rather than as a lane header;
                //   · an empty lane was ~600px of blank page, indistinguishable from the page ending.
                // The tint is `bg-muted/30` rather than a literal: the cards inside are `bg-card`
                // (white), so the lane has to sit BEHIND them in both themes, and a hard-coded
                // near-white would invert wrongly in dark.
                className="flex min-w-0 flex-col overflow-hidden rounded-xl border bg-muted/30"
              >
                {/* No background of its own: the section already paints `bg-muted/30`, and repeating it here
                      would composite 30% over 30% and make the header a visibly different tone from
                      its own lane. The mockup's header and lane are the SAME colour, separated by the
                      rule alone. */}
                <div
                  className={cn(
                    'space-y-1 border-b px-3 py-2',
                    // With the title row visually hidden the bar carries only the blurb; without this
                    // it reads as an empty band above the cards.
                    shownLanes.length === 1 && 'py-1.5',
                  )}
                >
                  {/* `w-fit` + `gap-2`, NOT justify-between: the count belongs to the title, and at a
                      386px column width "space between" is 386px of separation.

                      ⚠️ `sr-only` WHEN ONLY ONE LANE RENDERS — which is every phone view. The chip
                      strip directly above already reads "Queued 36" in the selected state, so this row
                      repeated it verbatim 59px later, in the most contested space on the page.
                      `sr-only` and NOT `hidden`: the h2 is this section's `aria-labelledby` target, so
                      it has to stay in the accessibility tree — a screen reader still gets a named
                      region, a sighted operator stops reading the same words twice.
                      ⚠️ Only this ROW is hidden. The blurb below still renders, and so does
                      `noteWhenReprintCheckFailed` — that note exists precisely BECAUSE the page-level
                      banner is several screens above on a phone, so hiding the whole header here would
                      re-open the exact hole it was added to close. */}
                  <div
                    className={cn(
                      'flex w-fit items-center gap-2',
                      shownLanes.length === 1 && 'sr-only',
                    )}
                  >
                    <h2 id={`lane-${lane.key}`} className="text-sm font-semibold">
                      {lane.title}
                    </h2>
                    {/* ⚠️ "{shown} of {total}" whenever a filter is on. A filter must never be able to
                        make work look FINISHED — a lane reading a bare "0" because of a search box two
                        screens up is the board telling the operator there is nothing to do. */}
                    <Badge variant="outline" className={lane.badgeClassName}>
                      {filtering ? `${orders.length} of ${total}` : orders.length}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">{lane.blurb}</p>
                  {/* The caveat has to reach the lane it invalidates. On a phone the page-level banner
                      is several screens above this, so by the time the operator is reading an empty
                      green lane the only explanation is off-screen. */}
                  {board.reprintCheckFailed && lane.noteWhenReprintCheckFailed && (
                    <p className="rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
                      {lane.noteWhenReprintCheckFailed}
                    </p>
                  )}
                </div>
                <div
                  className={cn(
                    'p-2',
                    shownLanes.length === 1
                      ? 'grid items-start gap-2 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4'
                      : // ⚠️ THE LANE BODY SCROLLS, THE PAGE DOES NOT. Measured live at 1512px: 67
                        // cards made the page 4,984px tall — 5.2 screens — because every lane grew
                        // without limit and the tallest one set the height of the board. Capping each
                        // lane at roughly a viewport keeps the board's SHAPE (which is what a board is
                        // for) on one screen. `max-h` only in the side-by-side layout: with a single
                        // lane selected it owns the full width and there is nothing to keep aligned.
                        'max-h-[calc(100vh-20rem)] space-y-2 overflow-y-auto',
                  )}
                >
                  {orders.length === 0 ? (
                    <p className="rounded-md border border-dashed px-3 py-6 text-center text-xs text-muted-foreground sm:col-span-full">
                      {/* "None" would be a lie under a filter — the lane may be full. */}
                      {filtering && total > 0 ? 'No matches in this lane' : 'None'}
                    </p>
                  ) : (
                    orders.map((order) => (
                      <OrderCard
                        key={`${order.storeId}#${order.orderId}`}
                        order={order}
                        nowMs={nowMs}
                      />
                    ))
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

function OrderCard({ order, nowMs }: { order: ProductionBoardOrder; nowMs: number }) {
  const due = dueStatus(order.dueDate, nowMs);
  const sheetCount = order.batchIds.length;
  const brand = order.brandLabel ?? order.storeId;
  const pieceLine = productionPieceLine(order.production);

  return (
    // ⚠️ THE CARD IS NO LONGER ONE BIG ANCHOR (PR-9). It had to stop being one: a sheet chip is itself a
    // link, and `<a>` inside `<a>` is invalid HTML — browsers silently un-nest it, so the chip would
    // have rendered outside the card it belongs to. The order NAME is now the anchor, stretched over the
    // whole card by `after:absolute after:inset-0`, so the entire card stays tappable (which matters far
    // more on a phone than the markup does) while the chips sit above it on `relative z-10`.
    //
    // This also fixes the accessible name for free. As one anchor, the card's computed name was the
    // concatenation of everything inside it — "#1042 Rush 3d overdue 1 to reprint — don't ship
    // low-trendz 5h 3 batched · 1 printed 2 sheets" — per card, in a screen reader's links list. Now the
    // link's name is just the order and its brand, and the sheet chips are separate, individually named
    // links. (Still not a `<div onClick>`: that trips noStaticElementInteractions AND
    // useKeyWithClickEvents, both error-level in `biome ci`, and loses right-click / open-in-new-tab.)
    //
    // ⚠️ `gap-0` is load-bearing, not cosmetic. The Card primitive is `flex flex-col gap-6 … py-6`;
    // twMerge resolves the `p-3` override against `py-6` but leaves `gap-6` untouched, so each of the
    // card's children got 24px of flex gap ON TOP of the `mt-2`/`mt-1` margins the author intended.
    // ~48px of accidental height per card — on the single-column phone board that roughly doubled the
    // total scroll, and it made the count line read as detached from its own card.
    //
    // `active:` matters because there is no hover on touch: a tap gave no feedback at all before
    // navigation, which on a slow shop connection reads as a dead tap and invites a second one.
    // `focus-within:` replaces the ring the card carried as an anchor — the ring has to follow the
    // keyboard focus that is now on a child.
    <Card className="relative gap-0 p-3 transition-colors hover:bg-muted/50 active:bg-muted focus-within:ring-[3px] focus-within:ring-ring/50">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <h3 className="text-sm font-medium">
              <Link
                to="/orders/$storeId/$orderId"
                params={{ storeId: order.storeId, orderId: order.orderId }}
                search={orderCardSearch('production')}
                aria-label={`${order.orderName} — ${brand}`}
                className="after:absolute after:inset-0 after:rounded-xl focus-visible:outline-none"
              >
                {order.orderName}
              </Link>
            </h3>
            {/* Inline with the order number, as the mockup has it — on its own line it cost ~18px on
                every card, which is a whole extra screen of scroll across a 67-card board. `min-w-0
                truncate` so a long brand shortens instead of pushing the badges out of the row. */}
            <span className="min-w-0 truncate text-xs text-muted-foreground">{brand}</span>
            {order.rush && (
              <Badge className="border-transparent bg-red-600 text-white hover:bg-red-600">
                Rush
              </Badge>
            )}
            {due && (
              <Badge
                variant="outline"
                className={cn(
                  'font-normal',
                  due.overdue
                    ? 'border-destructive/40 bg-destructive/10 text-destructive'
                    : 'text-muted-foreground',
                )}
              >
                {due.label}
              </Badge>
            )}
            {order.shippedUnproducedLines > 0 && (
              <Badge
                variant="outline"
                className="border-amber-400/50 bg-amber-100 font-normal text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
              >
                {order.shippedUnproducedLines} shipped · not printed
              </Badge>
            )}
            {/* ⚠️ The instruction is IN the badge, not in a `title=` tooltip. A tooltip needs hover,
                  and there is no gesture that shows one on a touch device — so on the phones the shop
                  floor actually uses, the whole meaning ("don't ship this yet") was unreachable and all
                  that rendered was an unexplained number. Radix Tooltip is not the fix either; it
                  deliberately does not open on touch. Shorter, too, which matters in a narrow lane. */}
            {order.reprintOutstanding > 0 && (
              <Badge
                variant="outline"
                className="border-amber-400/50 bg-amber-100 font-normal text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
              >
                {order.reprintOutstanding} to reprint — don’t ship
              </Badge>
            )}
          </div>
          {/* The brand name, not the slug — /batches, /press, the run sheet and /catalog all
                show "Low Trendz" and this board was the last surface printing `low-trendz` at an
                operator. Falls back to the slug, which is both today's rendering and what a
                registry-read failure degrades to. */}
        </div>
        <span className="shrink-0 text-xs text-muted-foreground">
          {relativeAge(order.shopifyCreatedAt, nowMs)}
        </span>
      </div>
      {/* GARMENTS first, then the per-line breakdown. "3 batched" never said whether that was 3
            shirts or 300, which is the difference between a ten-minute job and an afternoon. */}
      {pieceLine && <p className="mt-2 text-xs font-medium text-foreground">{pieceLine}</p>}
      <p className={cn('text-xs text-muted-foreground', pieceLine ? 'mt-0.5' : 'mt-2')}>
        {productionCountLine(order.production)}
      </p>
      {sheetCount > 0 && <SheetChips order={order} />}
    </Card>
  );
}

/**
 * The sheets this order's work sits on, as links (PR-9). Replaces the dead string "2 sheets", which
 * told an operator a sheet existed and then made them go and find it.
 *
 * ⚠️ THE COUNT COMES FROM `batchIds`, THE LINKS FROM `sheets`. They are not the same length when the
 * batch lookup partially failed, and the honest rendering of that is "we know there are 2 sheets, here
 * is the 1 we could name" — never a silently shorter list, which would read as "this order has 1 sheet"
 * and send someone looking for work that is already accounted for.
 */
function SheetChips({ order }: { order: ProductionBoardOrder }) {
  const sheets = order.sheets ?? [];
  const unnamed = order.batchIds.length - sheets.length;
  return (
    <div className="relative z-10 mt-1 flex flex-wrap items-center gap-1">
      {sheets.map((s) => (
        <Link
          key={s.batchId}
          to="/batches"
          search={{ batch: s.batchId }}
          // ⚠️ `relative z-10` ON THE WRAPPER IS LOAD-BEARING AND NO TEST HERE CAN SEE IT. The card's
          // stretched anchor paints an overlay across the whole card, so without its own stacking
          // context the chip is UNDER it. Measured in Chrome via elementFromPoint at the chip's centre:
          // with the class, a click resolves to `/batches?batch=b1`; without it, to `/order` — the
          // sheet link is simply dead, and every jsdom assertion still passes (both mutations survived
          // the suite). Same for `after:absolute after:inset-0` on the name link: removing it leaves
          // the card body hitting NO ANCHOR at all, i.e. only the order-name text stays tappable.
          // Phone-first: 44px under a thumb, 32px on a desktop where the pointer is exact. `min-h-8`
          // alone was 32px everywhere, under the floor /count (44) and /scan (56) already set.
          className="inline-flex min-h-11 items-center rounded-md border px-2 text-xs text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 md:min-h-8"
          aria-label={`${sheetLabel(s)} — ${SHEET_STATUS_LABEL[s.status] ?? s.status}`}
        >
          <span className="font-medium text-foreground">{sheetLabel(s)}</span>
          <span className="ml-1">· {SHEET_STATUS_LABEL[s.status] ?? s.status}</span>
        </Link>
      ))}
      {/* Never let a failed lookup shrink the sheet count silently. */}
      {unnamed > 0 && (
        <span className="text-xs text-muted-foreground">
          {sheets.length > 0
            ? `+${unnamed} more sheet${unnamed === 1 ? '' : 's'}`
            : `${unnamed} sheet${unnamed === 1 ? '' : 's'}`}
        </span>
      )}
    </div>
  );
}
